import nxModule from 'nxModule';

import BigNumber from 'bignumber.js';
import {sum} from "../../../../shared/common/MathUtils";
import _ from "lodash";
import {amortizedFeeBalanceForLine, customFeeBalanceForLine} from './loan-custom-fees';

const templateUrl = require('./loan-pre-terminate.template.html');
nxModule.component('loanPreTerminate', {
  templateUrl: templateUrl,
  bindings: {
    maxValues: '<',
    priorityAmortizationIds: '<',
    formName: '=',
    paymentRequest: '=',
    waiveRequest: '=',
    officialReceipt: '=',
    collectOfficialReceipt: '<',
    waiveOnly: '<',
    loan: '<',
  },
  controller: function ($scope, $route, loanPreTerminateService, loanService, loanProductsCache) {
    let that = this;
    that.loanId = $route.current.params['loanId'];
    that.amortization = null;
    that.amortizationIds = null;
    that.customFeesTotalBalance = new BigNumber(0);
    that.isAsEarned = false;
    that.asEarnedInterest = new BigNumber(0);
    that.asEarnedAutomaticWaiveAmount = new BigNumber(0);

    that.$onInit = async () => {
      that.maxValues.pastDueInterestWithMaturity = bn(that.maxValues.pastDueInterest).plus(that.maxValues.pastDueMaturityInterest).dp(2).toNumber();
      that.maxValues.penaltyWithMaturity = bn(that.maxValues.penalty).plus(that.maxValues.penaltyMaturity).dp(2).toNumber();
      await fetchCustomFees();
      initializeWaivedFromWaiveRequest();
      that.updateTotalOutstandingBalance();
      that.updateCustomFeesParams();
      that.updateCustomFeesAmount();
      await that.setUpAsEarnedInterest();
    };

    that.setUpAsEarnedInterest = async() => {
      if (that.waiveOnly || !that.loan || that.loan.uidLedger || that.loan.asEarnedInterestCalculation) {
        return;
      }
      const loanTypes = await loanProductsCache.toPromise();
      const loanProduct = _.find(loanTypes, (d) => d.id === that.loan.typeId);
      if (loanProduct.asEarnedPreterminationInterestCalculation) {
        that.isAsEarned = true;
        that.asEarnedInterest = await loanPreTerminateService.getAsEarnedInterest(that.loan.id);
        that.asEarnedAutomaticWaiveAmount = that.maxValues.interest - that.asEarnedInterest;
        that.waived.interest = that.asEarnedAutomaticWaiveAmount;
      }
    }

    if (!that.waiveRequest.total) {
      that.waiveRequest.total = {
        totalWaive: 0,
        totalOutstandingBalance: 0,
        totalFinalBalanceToPay: 0
      };
    }

    that.finalValues = {
      principal: 0,
      interest: 0,
      cbu: 0,
      pf: 0,
      tp: 0,
      pastDueInterestWithMaturity: 0,
      penaltyWithMaturity: 0
    };

    that.waived = {
      principal: 0,
      interest: 0,
      cbu: 0,
      pf: 0,
      tp: 0,
      pastDueInterestWithMaturity: 0,
      penaltyWithMaturity: 0,
      custom: {
        fees: [],
        totalAmount: 0
      }
    };

    const initializeWaivedFromWaiveRequest = () => {
      that.waived = {
        principal: 0,
        interest: 0,
        cbu: 0,
        pf: 0,
        tp: 0,
        pastDueInterestWithMaturity: 0,
        penaltyWithMaturity: 0,
        custom: {
          fees: [],
          totalAmount: 0
        }
      };

      if (!that.waiveRequest?.waiveParts) {
        return;
      }

      that.waiveRequest.waiveParts.forEach(part => {
        switch (part.waiveType) {
          case 'INTEREST':
            that.waived.interest = bn(part.waiveAmount).toNumber();
            break;
          case 'CBU':
            that.waived.cbu = bn(part.waiveAmount).toNumber();
            break;
          case 'PF':
            that.waived.pf = bn(part.waiveAmount).toNumber();
            break;
          case 'TP':
            that.waived.tp = bn(part.waiveAmount).toNumber();
            break;
          case 'PAST_DUE_INTEREST':
            that.waived.pastDueInterestWithMaturity = bn(part.waiveAmount).toNumber();
            break;
          case 'PENALTY':
            that.waived.penaltyWithMaturity = bn(part.waiveAmount).toNumber();
            break;
          case 'CUSTOM_FEE':
            if (part.feeDefinitionId) {
              that.waived.custom.fees.push({
                feeDefinitionId: part.feeDefinitionId,
                amount: bn(part.waiveAmount).toNumber()
              });
            }
            break;
        }
      });

      that.waived.custom.totalAmount = that.waived.custom.fees.reduce((total, fee) => bn(total).plus(fee.amount).toNumber(), 0);
    }

    that.updateCustomFeesAmount = () => {
      if (that.customFees && that.waived?.custom?.fees) {
        that.customFees.forEach(fee => {
          const waivedFee = that.waived.custom.fees.find(
            waived => waived.feeDefinitionId === fee.feeDefinitionId
          );
          fee.waiveAmount = waivedFee ? waivedFee.amount : 0;
        });
      }
    };

    const bn = (number) => {
      return number ? new BigNumber(number) : new BigNumber(0)
    };

    that.buildWaiveParts = () => {
      if(!that.maxValues) {
        return;
      }

      let waiveRequests = [];
      if (that.finalValues.interest < that.maxValues.interest) {
        waiveRequests.push({
          waiveType: 'INTEREST',
          waiveAmount: bn(that.waived.interest),
        });
      }

      if (that.finalValues.penaltyWithMaturity < that.maxValues.penaltyWithMaturity) {
        waiveRequests.push({
          waiveType: 'PENALTY',
          waiveAmount: bn(that.waived.penaltyWithMaturity),
        });
      }

      if (that.finalValues.pastDueInterestWithMaturity < that.maxValues.pastDueInterestWithMaturity) {
        waiveRequests.push({
          waiveType: 'PAST_DUE_INTEREST',
          waiveAmount: bn(that.waived.pastDueInterestWithMaturity)
        });
      }

      if (that.finalValues.cbu < that.maxValues.cbuCharge) {
        waiveRequests.push({
          waiveType: 'CBU',
          waiveAmount: bn(that.waived.cbu)
        });
      }

      if (that.finalValues.pf < that.maxValues.pfCharge) {
        waiveRequests.push({
          waiveType: 'PF',
          waiveAmount: bn(that.waived.pf)
        });
      }

      if (that.finalValues.tp < that.maxValues.tpCharge) {
        waiveRequests.push({
          waiveType: 'TP',
          waiveAmount: bn(that.waived.tp)
        });
      }

      if (that.customFees) {
        that.customFees.forEach(fee => {
          if (bn(fee.finalBalance).isLessThanOrEqualTo(bn(fee.balance))) {
            waiveRequests.push({
              waiveType: 'CUSTOM_FEE',
              waiveAmount: bn(fee.waiveAmount),
              feeDefinitionId: fee.feeDefinitionId
            });
          }
        });
      }


      return waiveRequests;
    };

    that.updateWaiveRequest = () => {
      that.waiveRequest = {
        total: that.waiveRequest.total,
        productId: that.loan?.id,
        waiveParts: that.buildWaiveParts(),
        priorityAmortizationIds: that.priorityAmortizationIds
      };
    };

    that.updatePaymentRequest = () => {
      const fv = that.finalValues;

      that.paymentRequest = {
        productId: that.loanId,
        amount: that.waiveRequest.total.totalFinalBalanceToPay,

        principalAmount: fv.principal !== 0 ? fv.principal : undefined,
        interestAmount: fv.interest !== 0 ? fv.interest : undefined,
        cbuChargeAmount: fv.cbu !== 0 ? fv.cbu : undefined,
        pfChargeAmount: fv.pf !== 0 ? fv.pf : undefined,
        tpChargeAmount: fv.tp !== 0 ? fv.tp : undefined,
        pastDueInterestAmount: fv.pastDueInterestWithMaturity !== 0 ? fv.pastDueInterestWithMaturity : undefined,
        penaltyAmount: fv.penaltyWithMaturity !== 0 ? fv.penaltyWithMaturity : undefined,
        commandPurpose: 'LOAN_WAVE'
      };
    };

    that.updateTotalWaive = () => {
      if (that.waived) {
        that.waiveRequest.total.totalWaive = parseFloat(bn(that.waived.interest)
        .plus(bn(that.waived.cbu))
        .plus(bn(that.waived.pf))
        .plus(bn(that.waived.tp))
        .plus(bn(that.waived.pastDueInterestWithMaturity))
        .plus(bn(that.waived.penaltyWithMaturity))
        .plus(that.calculateTotalWaivedCustomFees())
        .toFixed(2));
      }
    };

    that.updateTotalFinalBalance = () => {
      that.waiveRequest.total.totalFinalBalanceToPay = parseFloat(bn(that.maxValues.principal)
        .plus(bn(that.finalValues.interest))
        .plus(bn(that.finalValues.cbu))
        .plus(bn(that.finalValues.pf))
        .plus(bn(that.finalValues.tp))
        .plus(bn(that.finalValues.pastDueInterestWithMaturity))
        .plus(bn(that.finalValues.penaltyWithMaturity))
        .plus(bn(that.customFeesTotalFinalBalance))
        .toFixed(2));
    };

    that.updateFinalBalances = () => {
      if (that.maxValues) {

        if (that.maxValues.interest) {
          that.finalValues.interest = parseFloat(bn(that.maxValues.interest).minus(bn(that.waived.interest)).toFixed(2));
        }

        if (that.maxValues.cbuCharge) {
          that.finalValues.cbu = parseFloat(bn(that.maxValues.cbuCharge).minus(bn(that.waived.cbu)).toFixed(2));
        }

        if (that.maxValues.pfCharge) {
          that.finalValues.pf = parseFloat(bn(that.maxValues.pfCharge).minus(bn(that.waived.pf)).toFixed(2));
        }

        if (that.maxValues.tpCharge) {
          that.finalValues.tp = parseFloat(bn(that.maxValues.tpCharge).minus(bn(that.waived.tp)).toFixed(2));
        }
        
        if (that.maxValues.pastDueInterestWithMaturity) {
          that.finalValues.pastDueInterestWithMaturity = parseFloat(bn(that.maxValues.pastDueInterestWithMaturity).minus(bn(that.waived.pastDueInterestWithMaturity)).toFixed(2));
        }

        if (that.maxValues.penaltyWithMaturity) {
          that.finalValues.penaltyWithMaturity = parseFloat(bn(that.maxValues.penaltyWithMaturity).minus(bn(that.waived.penaltyWithMaturity)).toFixed(2));
        }
        
        that.updateTotalFinalBalance();
      }
    };

    that.updateCustomFeeBalance = () => {
      if (that.customFees) {
        that.customFees.forEach(fee => {
          // fee.finalBalance = parseFloat(bn(fee.balance).minus(bn(fee.waiveAmount))).toFixed(2);
          fee.finalBalance = Math.round((parseFloat(fee.balance) - parseFloat(fee.waiveAmount)) * 100) / 100;
        });

        that.customFeesTotalFinalBalance = that.customFees.reduce((acc, fee) => acc + Number(fee.finalBalance || 0), 0);
      }
    };

    that.updateTotalOutstandingBalance = () => {
      that.waiveRequest.total.totalOutstandingBalance = parseFloat(bn(that.maxValues.principal)
        .plus(bn(that.maxValues.interest))
        .plus(bn(that.maxValues.cbuCharge))
        .plus(bn(that.maxValues.pfCharge))
        .plus(bn(that.maxValues.tpCharge))
        .plus(bn(that.maxValues.pastDueInterestWithMaturity))
        .plus(bn(that.maxValues.penaltyWithMaturity))
        .plus(bn(that.customFeesTotalBalance))
        .toFixed(2));
    };

    that.calculateTotalWaivedCustomFees = () => {
      if (!Array.isArray(that.customFees)) {
        return 0;
      }
      return that.customFees.reduce((acc, fee) => acc + Number(fee.waiveAmount || 0), 0);
    };

    that.updateCustomFeesParams = () => {
      that.updateCustomFeeBalance();
      that.updateFinalBalances();
      that.updateTotalWaive();
      that.updateWaiveRequest();
      that.updatePaymentRequest();
    };

    // Line number of the single amortization being waived, or null when the whole loan is
    // in scope - then every fee instance of a definition counts towards its balance.
    const waivedAmortizationLineNumber = () => {
      if (!that.waiveOnly || !that.loan || _.size(that.priorityAmortizationIds) !== 1) {
        return null;
      }

      const amortizationId = Number(that.priorityAmortizationIds[0]);
      const amortizationToWaive = that.loan.amortizationSchedule.list.find(amortization => amortization.id === amortizationId);
      return amortizationToWaive ? amortizationToWaive.lineNumber : null;
    };

    const fetchCustomFees = async () => {

      // handle case for pretermination (or if that.loan isn't null)
      if (that.loan) {
        const [persistedFees, terminationFees] = await Promise.all([
          loanService.getLoanFees(that.loan.id),
          loanPreTerminateService.getCustomPreterminationFees(that.loan.id)
        ]);

        const lineNumberToWaive = waivedAmortizationLineNumber();

        // by default, un-applied non-termination fees are included for waiving
        // Fees applied on LOAN_PAYMENT will be added later - there is a need to combine some of them before adding to final list
        that.customFees = _.filter(persistedFees, fee => !['LOAN_PRETERMINATION', 'LOAN_RELEASE', 'LOAN_PAYMENT', 'FORMULA'].includes(fee.applyOn) && !fee.appliedDate);

        // Each amortization might have its own instance of fee - we have to combine them into one 'virtual' fee for each present fee definition
        _.chain(persistedFees)
          .filter(fee => fee.applyOn === 'LOAN_PAYMENT')
          .groupBy(fee => fee.feeDefinitionId)
          .forEach(fees => {
            that.customFees.push({
              feeName: fees[0].feeName,
              feeDefinitionId: fees[0].feeDefinitionId,
              applyOn: 'LOAN_PAYMENT',
              balance: customFeeBalanceForLine(fees, lineNumberToWaive)
            });
          })
          .value();

        if (!that.waiveOnly) {
          that.customFees.push(...terminationFees);
        }

        // handle case for waiving a single amortization - a fee spread across the schedule
        // holds only a part of its balance on that line
        if (lineNumberToWaive !== null) {
          const amortizedFees = await loanService.getAmortizedFees(that.loanId);
          that.customFees
            .filter(fee => fee.applyOn === 'LOAN_AMORTIZATION')
            .forEach(fee => {
              fee.balance = amortizedFeeBalanceForLine(amortizedFees, fee.id, lineNumberToWaive);
            });
        }
      }

      that.customFees.forEach(fee => {
        fee.waiveAmount = 0;
        fee.finalBalance = fee.balance;
      });

      const totalBalance = sum(that.customFees.map(fee => fee.balance));
      that.customFeesTotalBalance = that.customFeesTotalBalance.plus(totalBalance);
    };

    that.$onChanges = change => {
      if (change.maxValues && change.maxValues.currentValue) {
        that.maxValues.pastDueInterestWithMaturity = bn(that.maxValues.pastDueInterest).plus(that.maxValues.pastDueMaturityInterest).dp(2).toNumber();
        that.maxValues.penaltyWithMaturity = bn(that.maxValues.penalty).plus(that.maxValues.penaltyMaturity).dp(2).toNumber();

        that.finalValues.principal = that.maxValues.principal;
        that.finalValues.interest = that.maxValues.interest;
        that.finalValues.cbu = that.maxValues.cbuCharge;
        that.finalValues.pf = that.maxValues.pfCharge;
        that.finalValues.tp = that.maxValues.tpCharge;
        that.finalValues.pastDueInterestWithMaturity = that.maxValues.pastDueInterestWithMaturity;
        that.finalValues.penaltyWithMaturity = that.maxValues.penaltyWithMaturity;
        that.updateTotalFinalBalance();

        that.updateTotalOutstandingBalance();
      }

      that.updatePaymentRequest();
    };

    $scope.$watchGroup(
      ['$ctrl.waived.interest',
        '$ctrl.waived.cbu',
        '$ctrl.waived.pf',
        '$ctrl.waived.tp',
        '$ctrl.waived.pastDueInterestWithMaturity',
        '$ctrl.waived.penaltyWithMaturity'],
      () => {
        that.updateCustomFeesParams();
      });
  }
});
